import { useState } from "react";
import axios from "axios";
import { useLang } from "@/contexts/LangContext";
import SectionReveal from "@/components/SectionReveal";
import {
  Plane,
  Hotel,
  Car,
  Search,
  CheckCircle2,
  ShieldCheck,
  CreditCard,
  Loader2,
  ArrowRight,
  Users,
  Calendar,
  MapPin,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SIMPLIFLY_URL = "https://www.simplifly.tech/flight/booking";

const TABS = [
  { id: "flight", icon: Plane, label: "Flights" },
  { id: "hotel", icon: Hotel, label: "Hotels" },
  { id: "transfer", icon: Car, label: "Transfers" },
];

const DEPOSIT_TIERS = [
  {
    id: "explorer",
    price: "$99",
    name: "Explorer",
    tag: "Custom itinerary planning slot",
    includes: [
      "AI-drafted multi-day plan",
      "Human curator review (within 48h)",
      "Visa & arrival guidance",
      "Refundable up to 14 days before departure",
    ],
  },
  {
    id: "premium",
    price: "$299",
    name: "Premium",
    tag: "Mandarin · Medical · Cultural tours",
    badge: "Most popular",
    includes: [
      "Everything in Explorer",
      "Pre-booked language school / clinic visit",
      "Bilingual airport pickup (1 city)",
      "WhatsApp concierge during trip",
      "Priority hotel partner rates",
    ],
  },
  {
    id: "vip",
    price: "$999",
    name: "VIP",
    tag: "Canton Fair · Business · Bespoke",
    includes: [
      "Everything in Premium",
      "Dedicated multi-city interpreter",
      "Factory visit & supplier matching",
      "Executive sedan transfers (3 cities)",
      "24/7 dedicated trip manager",
    ],
  },
];

const BookYourTrip = () => {
  const { t, lang } = useLang();
  const tt = t.booking || {};
  const [tab, setTab] = useState("flight");

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    origin: "Dubai (DXB)",
    destination: "Beijing (PEK)",
    travel_date: "",
    return_date: "",
    check_in: "",
    check_out: "",
    city: "Beijing",
    arrival_city: "",
    passengers: 2,
    notes: "",
  });
  const [inquirySent, setInquirySent] = useState(false);
  const [inquiryError, setInquiryError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [depositPkg, setDepositPkg] = useState("premium");
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositError, setDepositError] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFlightSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams({
      from: form.origin,
      to: form.destination,
      depart: form.travel_date,
      return: form.return_date,
      pax: String(form.passengers),
      ref: "the-china-explorer",
    });
    window.open(`${SIMPLIFLY_URL}?${params.toString()}`, "_blank", "noopener");
  };

  const submitInquiry = async (type) => {
    setLoading(true);
    setInquirySent(false);
    setInquiryError(null);
    try {
      const payload = {
        type,
        name: form.name,
        email: form.email,
        phone: form.phone,
        language: lang,
        passengers: Number(form.passengers) || 1,
        notes: form.notes,
        ...(type === "hotel"
          ? { city: form.city, check_in: form.check_in, check_out: form.check_out }
          : { arrival_city: form.arrival_city || form.city, travel_date: form.travel_date }),
      };
      await axios.post(`${API}/booking-inquiry`, payload);
      setInquirySent(true);
    } catch (err) {
      console.error(err);
      setInquiryError(err?.response?.data?.detail || "Could not send inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const startDeposit = async () => {
    setDepositLoading(true);
    setDepositError(null);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        package_id: depositPkg,
        origin_url: window.location.origin,
        customer_email: form.email || undefined,
        customer_name: form.name || undefined,
        note: form.notes || undefined,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        setDepositError("Could not start checkout. Please try again.");
      }
    } catch (err) {
      console.error(err);
      setDepositError(err?.response?.data?.detail || "Payment could not start.");
    } finally {
      setDepositLoading(false);
    }
  };

  return (
    <section id="book" data-testid="book-section" className="bg-[#F7F5F0] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionReveal className="max-w-3xl">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#0E2A5C] font-semibold mb-4">
            ✦ {tt.eyebrow || "Book Your Trip"}
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#121212] leading-[1.05]">
            {tt.title || "Flights, hotels & transfers — all in one place"}
          </h2>
          <p className="mt-6 text-lg text-[#4A4A4A] leading-relaxed">
            {tt.subtitle ||
              "Search live flight inventory through our partner Simplifly, or request a custom quote for hotels and chauffeur transfers across China."}
          </p>
        </SectionReveal>

        {/* === OTA-style unified search widget === */}
        <SectionReveal delay={0.15} className="mt-12">
          <div className="bg-white shadow-2xl shadow-black/10 border border-black/5">
            {/* Tabs inside the widget */}
            <div className="flex border-b border-black/10 overflow-x-auto">
              {TABS.map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  data-testid={`book-tab-${id}`}
                  onClick={() => {
                    setTab(id);
                    setInquirySent(false);
                    setInquiryError(null);
                  }}
                  className={`flex items-center gap-2 px-6 md:px-8 py-5 text-sm uppercase tracking-wider whitespace-nowrap transition-colors border-b-2 -mb-px ${
                    tab === id
                      ? "border-[#C62828] text-[#C62828] font-semibold bg-[#C62828]/5"
                      : "border-transparent text-[#4A4A4A] hover:text-[#0E2A5C] hover:bg-black/[0.02]"
                  }`}
                >
                  <Icon size={16} /> {tt.tabs?.[id] || label}
                </button>
              ))}
            </div>

            {/* Search row — OTA style: inline horizontal fields, single CTA */}
            {tab === "flight" && (
              <form
                data-testid="flight-search-form"
                onSubmit={handleFlightSearch}
                className="p-5 md:p-7"
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-0 md:divide-x md:divide-black/10 md:border md:border-black/10 md:rounded-md md:overflow-hidden">
                  <WidgetField icon={<MapPin size={14} />} label={tt.from || "From"} className="md:col-span-3">
                    <input
                      data-testid="flight-from"
                      className="ip-widget"
                      value={form.origin}
                      onChange={(e) => update("origin", e.target.value)}
                      required
                    />
                  </WidgetField>
                  <WidgetField icon={<MapPin size={14} />} label={tt.to || "To"} className="md:col-span-3">
                    <input
                      data-testid="flight-to"
                      className="ip-widget"
                      value={form.destination}
                      onChange={(e) => update("destination", e.target.value)}
                      required
                    />
                  </WidgetField>
                  <WidgetField icon={<Calendar size={14} />} label={tt.depart || "Departure"} className="md:col-span-2">
                    <input
                      data-testid="flight-depart"
                      type="date"
                      className="ip-widget"
                      value={form.travel_date}
                      onChange={(e) => update("travel_date", e.target.value)}
                      required
                    />
                  </WidgetField>
                  <WidgetField icon={<Calendar size={14} />} label={tt.return || "Return"} className="md:col-span-2">
                    <input
                      data-testid="flight-return"
                      type="date"
                      className="ip-widget"
                      value={form.return_date}
                      onChange={(e) => update("return_date", e.target.value)}
                    />
                  </WidgetField>
                  <WidgetField icon={<Users size={14} />} label={tt.passengers || "Passengers"} className="md:col-span-2">
                    <input
                      data-testid="flight-passengers"
                      type="number"
                      min={1}
                      max={9}
                      className="ip-widget"
                      value={form.passengers}
                      onChange={(e) => update("passengers", e.target.value)}
                    />
                  </WidgetField>
                </div>
                <div className="mt-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <p className="text-xs text-[#4A4A4A]/80">
                    {tt.poweredBy || "Live inventory via Simplifly · opens in a new tab"}
                  </p>
                  <button
                    data-testid="flight-search-button"
                    type="submit"
                    className="group bg-[#C62828] hover:bg-[#B71C1C] text-white px-8 py-4 uppercase tracking-wider text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors"
                  >
                    <Search size={16} /> {tt.searchFlights || "Search flights"}
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </form>
            )}

            {tab === "hotel" && (
              <InquiryRow
                testidPrefix="hotel"
                form={form}
                update={update}
                onSubmit={() => submitInquiry("hotel")}
                loading={loading}
                inquirySent={inquirySent}
                inquiryError={inquiryError}
                tt={tt}
                fields={[
                  { key: "city", label: tt.city || "City in China", icon: <MapPin size={14} />, type: "text", required: true, span: "md:col-span-3" },
                  { key: "check_in", label: tt.checkIn || "Check-in", icon: <Calendar size={14} />, type: "date", required: true, span: "md:col-span-3" },
                  { key: "check_out", label: tt.checkOut || "Check-out", icon: <Calendar size={14} />, type: "date", required: true, span: "md:col-span-3" },
                  { key: "passengers", label: tt.guests || "Guests", icon: <Users size={14} />, type: "number", span: "md:col-span-3" },
                ]}
              />
            )}

            {tab === "transfer" && (
              <InquiryRow
                testidPrefix="transfer"
                form={form}
                update={update}
                onSubmit={() => submitInquiry("transfer")}
                loading={loading}
                inquirySent={inquirySent}
                inquiryError={inquiryError}
                tt={tt}
                fields={[
                  { key: "arrival_city", label: tt.pickupCity || "Pickup city", icon: <MapPin size={14} />, type: "text", required: true, span: "md:col-span-4" },
                  { key: "travel_date", label: tt.pickupDate || "Pickup date", icon: <Calendar size={14} />, type: "date", required: true, span: "md:col-span-4" },
                  { key: "passengers", label: tt.passengers || "Passengers", icon: <Users size={14} />, type: "number", span: "md:col-span-4" },
                ]}
              />
            )}
          </div>
        </SectionReveal>

        {/* === Reserve / deposit panel with WHAT'S INCLUDED === */}
        <SectionReveal delay={0.25} className="mt-20">
          <div className="bg-[#0E2A5C] text-white p-8 md:p-14">
            <div className="grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-5">
                <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-3">
                  ✦ {tt.reserveEyebrow || "Reserve your spot"}
                </p>
                <h3 className="font-heading text-3xl md:text-4xl leading-tight">
                  {tt.reserveTitle || "Pay your trip deposit securely"}
                </h3>
                <p className="mt-5 text-white/75 leading-relaxed">
                  {tt.reserveSubtitle ||
                    "Lock in your bespoke itinerary with a refundable deposit. The deposit covers planning, supplier reservations and your personal trip manager — it is fully credited toward your final invoice."}
                </p>

                <div className="mt-6 flex flex-wrap gap-3">
                  <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/15 px-3.5 py-2 text-xs uppercase tracking-wider">
                    <ShieldCheck size={14} className="text-[#1FA3E0]" /> Stripe secured
                  </span>
                  <span className="inline-flex items-center gap-2 bg-[#3BFFC2] text-[#0E2A5C] px-3.5 py-2 text-xs uppercase tracking-wider font-bold">
                    tabby · Pay in 4 (soon)
                  </span>
                  <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/15 px-3.5 py-2 text-xs uppercase tracking-wider">
                    <CreditCard size={14} className="text-[#1FA3E0]" /> Visa · MC · Amex
                  </span>
                </div>

                <p className="mt-6 text-xs text-white/55 leading-relaxed">
                  {tt.depositFinePrint ||
                    "Refundable up to 14 days before departure. Charge is in USD; we'll send you a receipt by email."}
                </p>
              </div>

              {/* Tiers + What's included */}
              <div className="lg:col-span-7">
                <div className="grid sm:grid-cols-3 gap-3">
                  {DEPOSIT_TIERS.map((d) => {
                    const active = depositPkg === d.id;
                    return (
                      <button
                        key={d.id}
                        data-testid={`deposit-package-${d.id}`}
                        onClick={() => setDepositPkg(d.id)}
                        className={`relative text-left p-5 transition-all duration-300 border-2 ${
                          active
                            ? "bg-white text-[#0E2A5C] border-[#D4AF37] shadow-2xl shadow-black/30"
                            : "bg-white/5 border-white/15 hover:border-white/40"
                        }`}
                      >
                        {d.badge && (
                          <span className="absolute -top-2.5 right-3 bg-[#D4AF37] text-[#0E2A5C] text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
                            {d.badge}
                          </span>
                        )}
                        <p
                          className={`text-xs uppercase tracking-wider ${
                            active ? "text-[#C62828]" : "text-[#D4AF37]"
                          }`}
                        >
                          {d.name}
                        </p>
                        <p className="font-heading text-3xl mt-2">{d.price}</p>
                        <p
                          className={`text-xs mt-2 leading-snug ${
                            active ? "text-[#4A4A4A]" : "text-white/65"
                          }`}
                        >
                          {d.tag}
                        </p>
                      </button>
                    );
                  })}
                </div>

                {/* Included list for the selected tier */}
                <div
                  data-testid={`deposit-includes-${depositPkg}`}
                  className="mt-6 bg-white/[0.04] border border-white/10 p-6"
                >
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37] mb-4">
                    What's included
                  </p>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                    {DEPOSIT_TIERS.find((d) => d.id === depositPkg).includes.map((line, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2.5 text-sm text-white/85"
                      >
                        <CheckCircle2
                          size={16}
                          className="text-[#3BFFC2] mt-0.5 flex-shrink-0"
                        />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  <input
                    data-testid="deposit-name"
                    className="ip-dark"
                    placeholder={tt.yourName || "Your full name"}
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                  />
                  <input
                    data-testid="deposit-email"
                    type="email"
                    className="ip-dark"
                    placeholder={tt.yourEmail || "Your email"}
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                  />
                </div>

                {depositError && (
                  <p data-testid="deposit-error" className="mt-3 text-sm text-[#FFB4B4]">
                    {depositError}
                  </p>
                )}

                <button
                  data-testid="deposit-pay-button"
                  onClick={startDeposit}
                  disabled={depositLoading}
                  className="mt-6 group bg-[#D4AF37] hover:bg-[#bf9a2b] disabled:opacity-60 text-[#0E2A5C] px-8 py-4 uppercase tracking-wider text-sm font-bold inline-flex items-center gap-3 transition-colors"
                >
                  {depositLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> {tt.starting || "Starting checkout..."}
                    </>
                  ) : (
                    <>
                      {tt.payDeposit || `Pay ${DEPOSIT_TIERS.find((d) => d.id === depositPkg).price} deposit`}{" "}
                      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </SectionReveal>
      </div>

      <style>{`
        .ip-widget {
          width: 100%;
          background: transparent;
          border: 0;
          padding: 0;
          color: #121212;
          font-size: 0.95rem;
          font-weight: 500;
          outline: none;
        }
        .ip-widget::placeholder { color: rgba(0,0,0,0.4); }
        .ip-dark {
          width: 100%;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.18);
          padding: 0.85rem 1rem;
          color: white;
          font-size: 0.95rem;
          outline: none;
          transition: border-color 200ms;
        }
        .ip-dark::placeholder { color: rgba(255,255,255,0.5); }
        .ip-dark:focus { border-color: #D4AF37; }
      `}</style>
    </section>
  );
};

const WidgetField = ({ icon, label, children, className = "" }) => (
  <label
    className={`flex flex-col justify-center px-4 md:px-5 py-3 md:py-4 bg-white border md:border-0 border-black/10 cursor-text hover:bg-[#F7F5F0]/60 transition-colors ${className}`}
  >
    <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#4A4A4A] mb-1.5">
      {icon} {label}
    </span>
    {children}
  </label>
);

const InquiryRow = ({
  testidPrefix,
  fields,
  form,
  update,
  onSubmit,
  loading,
  inquirySent,
  inquiryError,
  tt,
}) => (
  <form
    data-testid={`${testidPrefix}-inquiry-form`}
    onSubmit={(e) => {
      e.preventDefault();
      onSubmit();
    }}
    className="p-5 md:p-7"
  >
    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-0 md:divide-x md:divide-black/10 md:border md:border-black/10 md:rounded-md md:overflow-hidden">
      {fields.map((f) => (
        <WidgetField key={f.key} icon={f.icon} label={f.label} className={f.span}>
          <input
            data-testid={`${testidPrefix}-${f.key}`}
            type={f.type}
            required={f.required}
            min={f.type === "number" ? 1 : undefined}
            className="ip-widget"
            value={form[f.key]}
            onChange={(e) => update(f.key, e.target.value)}
          />
        </WidgetField>
      ))}
    </div>

    <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
      <input
        data-testid={`${testidPrefix}-name`}
        type="text"
        required
        placeholder={tt.yourName || "Your name"}
        className="ip-flat"
        value={form.name}
        onChange={(e) => update("name", e.target.value)}
      />
      <input
        data-testid={`${testidPrefix}-email`}
        type="email"
        required
        placeholder={tt.yourEmail || "Email"}
        className="ip-flat"
        value={form.email}
        onChange={(e) => update("email", e.target.value)}
      />
      <input
        data-testid={`${testidPrefix}-phone`}
        type="tel"
        placeholder={tt.yourPhone || "WhatsApp / Phone"}
        className="ip-flat"
        value={form.phone}
        onChange={(e) => update("phone", e.target.value)}
      />
    </div>

    <textarea
      data-testid={`${testidPrefix}-notes`}
      rows={2}
      placeholder={tt.notes || "Notes (optional)"}
      className="ip-flat mt-3 resize-none"
      value={form.notes}
      onChange={(e) => update("notes", e.target.value)}
    />

    <div className="mt-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
      <div className="text-sm">
        {inquirySent && (
          <p data-testid={`${testidPrefix}-success`} className="inline-flex items-center gap-2 text-[#2E5D4B]">
            <CheckCircle2 size={16} />
            {tt.inquirySuccess || "Thank you! We'll be in touch within 24 hours."}
          </p>
        )}
        {inquiryError && (
          <p data-testid={`${testidPrefix}-error`} className="text-[#C62828]">
            {inquiryError}
          </p>
        )}
      </div>
      <button
        data-testid={`${testidPrefix}-submit`}
        type="submit"
        disabled={loading}
        className="bg-[#C62828] hover:bg-[#B71C1C] disabled:opacity-60 text-white px-8 py-4 uppercase tracking-wider text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors"
      >
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin" /> {tt.sending || "Sending..."}
          </>
        ) : (
          <>
            <Search size={16} /> {tt.requestQuote || "Request a quote"}
          </>
        )}
      </button>
    </div>

    <style>{`
      .ip-flat {
        width: 100%;
        background: #F7F5F0;
        border: 1px solid rgba(0,0,0,0.1);
        padding: 0.85rem 1rem;
        color: #121212;
        font-size: 0.95rem;
        outline: none;
        transition: border-color 200ms;
      }
      .ip-flat:focus { border-color: #C62828; }
    `}</style>
  </form>
);

export default BookYourTrip;
